import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import InputCst from "@components/InputCst/InputCst";
import LOPickerCst, { LOPickerSelectedItem } from "@components/LOPickerCst";
import {
  AlternativeLOSection,
  SelectedLOItem,
} from "@pages/Contents/V2/Corporate/Catalog/types";
import AddLOToPathModal, {
  AddLOSelectedItem,
} from "../Path/components/AddLOToPathModal";

type LOAlternativesPickerMode = "course" | "path";

interface LOAlternativesPickerModalProps<
  TItem extends SelectedLOItem = SelectedLOItem,
> {
  open: boolean;
  mode: LOAlternativesPickerMode;
  section?: AlternativeLOSection<TItem> | null;
  excludeIds: number[];
  onClose: () => void;
  onConfirm: (section: AlternativeLOSection<TItem>) => void;
}

const itemKey = (item: Pick<SelectedLOItem, "id" | "kind">): string =>
  `${item.kind}-${item.id}`;

const LOAlternativesPickerModal = <TItem extends SelectedLOItem>({
  open,
  mode,
  section,
  excludeIds,
  onClose,
  onConfirm,
}: LOAlternativesPickerModalProps<TItem>) => {
  const { t } = useTranslation("lo-alternatives");
  const { t: tWizard } = useTranslation(
    mode === "course" ? "wizard-course" : "wizard-path",
  );
  const [sectionTitle, setSectionTitle] = useState("");
  const [sectionTitleError, setSectionTitleError] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSectionTitle(section?.title ?? "");
    setSectionTitleError(false);
  }, [open, section]);

  const validateSectionTitle = () => {
    const valid = sectionTitle.trim().length > 0;
    setSectionTitleError(!valid);
    return valid;
  };

  const initialItems = useMemo(() => section?.items ?? [], [section]);
  const initialItemsByKey = useMemo(
    () => new Map(initialItems.map((item) => [itemKey(item), item])),
    [initialItems],
  );

  const titleInput = (
    <Box sx={{ mt: 1, mb: 2 }}>
      <InputCst
        id="lo-alternatives-section-title"
        label={`${t("section-title.label")} *`}
        placeholder={t("section-title.placeholder")}
        value={sectionTitle}
        onChange={(event) => {
          setSectionTitle(event.target.value);
          setSectionTitleError(false);
        }}
        error={sectionTitleError}
        errorMessage={sectionTitleError ? tWizard("errors.required") : ""}
        customWidth="100%"
        minWidth={false}
      />
    </Box>
  );

  const buildSection = (items: SelectedLOItem[]) => {
    const title = sectionTitle.trim();
    return {
      ...(section?.id == null ? {} : { id: section.id }),
      ...(title ? { title } : {}),
      ...(section?.locked == null ? {} : { locked: section.locked }),
      kind: "section" as const,
      items: items as TItem[],
    };
  };

  const handlePathConfirm = (selected: AddLOSelectedItem[]) => {
    const mappedItems = selected.map((item) => {
      const existing = initialItemsByKey.get(itemKey(item));
      return {
        ...existing,
        id: item.id,
        title: item.title,
        type: item.type,
        kind: item.kind,
        entity: item.entity,
        completion_criteria: null,
      } satisfies SelectedLOItem;
    });

    const selectedByKey = new Map(
      mappedItems.map((item) => [itemKey(item), item]),
    );
    const initialKeys = new Set(initialItems.map(itemKey));
    const items = [
      ...initialItems.flatMap((item) => {
        const selectedItem = selectedByKey.get(itemKey(item));
        return selectedItem ? [selectedItem] : [];
      }),
      ...mappedItems.filter((item) => !initialKeys.has(itemKey(item))),
    ];

    onConfirm(buildSection(items));
  };

  const handleCourseConfirm = (selected: LOPickerSelectedItem[]) => {
    const items = selected.map((item) => {
      const key = itemKey({ id: item.id, kind: "activity" });
      const existing = initialItemsByKey.get(key);
      return {
        ...existing,
        id: item.id,
        title: item.title,
        type: item.type,
        kind: "activity" as const,
        entity: item.entity,
        completion_criteria: null,
      } satisfies SelectedLOItem;
    });
    onConfirm(buildSection(items));
  };

  const modalTitle = section ? t("title.edit") : t("title.create");

  if (mode === "path") {
    return (
      <AddLOToPathModal
        open={open}
        onClose={onClose}
        title={modalTitle}
        excludeIds={excludeIds}
        initialSelectedItems={initialItems.map((item) => ({
          id: item.id,
          title: item.title,
          type: item.type,
          kind: item.kind,
          entity: item.entity,
        }))}
        contentBeforeSearch={titleInput}
        maxSelection={7}
        validateConfirm={validateSectionTitle}
        onConfirm={handlePathConfirm}
      />
    );
  }

  return (
    <LOPickerCst
      open={open}
      onClose={onClose}
      title={modalTitle}
      entityType="activity"
      excludeIds={excludeIds}
      initialSelectedItems={initialItems.map((item) => ({
        id: item.id,
        title: item.title,
        type: item.type,
        entity: item.entity,
      }))}
      contentBeforeSearch={titleInput}
      maxSelection={7}
      validateConfirm={validateSectionTitle}
      onConfirm={handleCourseConfirm}
    />
  );
};

export default LOAlternativesPickerModal;
